import React from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  History,
  Thermometer,
  Bug,
  CheckCircle2,
  CalendarDays,
  FlaskConical,
  BookOpen,
} from "lucide-react";
import useSWR from "swr";
import { getToday } from "@/lib/api";

const fetcher = () => getToday();

export default function Home() {
  const { data: todayData } = useSWR("today", fetcher, {
    refreshInterval: 30000,
  });

  const today = new Date();
  const dateLabel = today.toLocaleDateString("ca-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  // Dies de treball: dimarts (2) a dissabte (6). Diumenge/dilluns = tancat.
  const WORKING_DAYS = [2, 3, 4, 5, 6];
  const isWorkingDay = WORKING_DAYS.includes(today.getDay());

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      {/* Hero */}
      <section className="animate-fade-up">
        <div className="flex items-center gap-2 text-ink-soft">
          <CalendarDays size={16} />
          <span className="label-eyebrow">{dateLabel}</span>
        </div>
        <h1 className="mt-2 font-display text-4xl font-bold leading-tight text-ink sm:text-5xl">
          Registres
          <span className="block text-terracotta">de la parada</span>
        </h1>
        <p className="mt-3 max-w-md text-base leading-relaxed text-ink-soft">
          Anota els controls APPCC del dia o consulta l'històric. Tot en un sol
          lloc, ràpid i sense embolics.
        </p>
      </section>

      {/* Daily status mini-cards */}
      {!isWorkingDay && (
        <div className="mt-6 rounded-2xl border border-line bg-muted px-4 py-3 text-sm text-ink-soft animate-fade-up">
          Avui <span className="font-semibold text-ink">tancat</span> · Obrim de dimarts a dissabte. No cal registrar res.
        </div>
      )}
      <section className="mt-8 grid grid-cols-2 gap-3 animate-fade-up" style={{ animationDelay: "60ms" }}>
        <DailyStatusCard
          icon={<Thermometer size={18} />}
          label="Temperatures"
          done={todayData?.temperature_done}
          closed={!isWorkingDay}
          testid="daily-status-temperature"
        />
        <DailyStatusCard
          icon={<Bug size={18} />}
          label="Plagues"
          done={todayData?.pest_done}
          closed={!isWorkingDay}
          testid="daily-status-pest"
        />
      </section>

      {/* Main actions */}
      <section className="mt-8 space-y-4">
        <Link
          to="/fill"
          data-testid="action-fill-records"
          className="group block animate-fade-up rounded-2xl border border-line bg-white p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft sm:p-7"
          style={{ animationDelay: "120ms" }}
        >
          <div className="flex items-center gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-terracotta text-white sm:h-16 sm:w-16">
              <ClipboardCheck size={26} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-terracotta">Avui</div>
              <div className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl">
                Omplir el registre que toca
              </div>
              <div className="mt-1 text-sm text-ink-soft">
                Temperatures, plagues, recepció…
              </div>
            </div>
            <div className="text-2xl text-ink-soft transition-transform group-hover:translate-x-1">
              →
            </div>
          </div>
        </Link>

        <Link
          to="/history"
          data-testid="action-view-history"
          className="group block animate-fade-up rounded-2xl border border-line bg-white p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft sm:p-7"
          style={{ animationDelay: "180ms" }}
        >
          <div className="flex items-center gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-ink text-white sm:h-16 sm:w-16">
              <History size={26} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow">Històric</div>
              <div className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl">
                Veure registres anteriors
              </div>
              <div className="mt-1 text-sm text-ink-soft">
                Filtra per tipus i per dates
              </div>
            </div>
            <div className="text-2xl text-ink-soft transition-transform group-hover:translate-x-1">
              →
            </div>
          </div>
        </Link>

        <Link
          to="/production"
          data-testid="action-production"
          className="group block animate-fade-up rounded-2xl border border-line bg-white p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft sm:p-7"
          style={{ animationDelay: "240ms" }}
        >
          <div className="flex items-center gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-forest text-white sm:h-16 sm:w-16">
              <FlaskConical size={26} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-forest">Producció</div>
              <div className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl">
                Fitxes de producció
              </div>
              <div className="mt-1 text-sm text-ink-soft">
                Elabora i apunta el que has fet
              </div>
            </div>
            <div className="text-2xl text-ink-soft transition-transform group-hover:translate-x-1">
              →
            </div>
          </div>
        </Link>

        <Link
          to="/plans"
          data-testid="action-plans"
          className="group block animate-fade-up rounded-2xl border border-line bg-white p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft sm:p-7"
          style={{ animationDelay: "300ms" }}
        >
          <div className="flex items-center gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-ink text-white sm:h-16 sm:w-16">
              <BookOpen size={26} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow">Autocontrol</div>
              <div className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl">
                Plans APPCC
              </div>
              <div className="mt-1 text-sm text-ink-soft">
                Els 8 plans, la seva documentació i registres
              </div>
            </div>
            <div className="text-2xl text-ink-soft transition-transform group-hover:translate-x-1">
              →
            </div>
          </div>
        </Link>
      </section>

      <footer className="mt-14 border-t border-line pt-6 text-center text-xs text-ink-soft">
        Sublims · Mercat Sant Antoni · Registres APPCC
      </footer>
    </div>
  );
}

function DailyStatusCard({ icon, label, done, closed, testid }) {
  return (
    <div
      data-testid={testid}
      className={
        "rounded-2xl border p-4 transition-colors " +
        (closed
          ? "border-line bg-muted/50"
          : done
          ? "border-forest/20 bg-forest-bg"
          : "border-line bg-white")
      }
    >
      <div className="flex items-center justify-between">
        <div className={closed ? "text-ink-soft/50" : done ? "text-forest" : "text-ink-soft"}>{icon}</div>
        {closed ? (
          <span className="text-[10px] font-bold uppercase tracking-widest text-ink-soft/60">Tancat</span>
        ) : done ? (
          <CheckCircle2 size={18} className="text-forest" />
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-widest text-ink-soft">
            Pendent
          </span>
        )}
      </div>
      <div className={"mt-2 font-display text-base font-semibold " + (closed ? "text-ink-soft" : "text-ink")}>
        {label}
      </div>
      <div className={"text-xs " + (closed ? "text-ink-soft/60" : done ? "text-forest" : "text-ink-soft")}>
        {closed ? "Dia no laborable" : done ? "Fet avui" : "Sense registrar"}
      </div>
    </div>
  );
}
